import { OrganizationList } from '@/components'
import { usePositionStorage } from '@/hooks'
import { extractErrorMessage } from '@/lib'
import {
	AuthErrorAlert,
	AuthInput,
	AuthLayout,
	AuthSubmitButton,
	EmailVerificationForm,
	JobillaBrandLogo,
	logger,
} from '@/ui'
import { authSelectOrganisation, useAuthSetPasswordByInvitation } from '@jobilla/nexus-sdk'
import type { AuthSelectOrganisationError, AuthSetPasswordByInvitation403 } from '@jobilla/nexus-sdk'
import { createFileRoute } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const setupSearchSchema = z.object({
	invitation_token: z.string(),
	email: z.string(),
	organisation_name: z.string(),
})

export const Route = createFileRoute('/account-setup')({
	validateSearch: setupSearchSchema,
	component: InviteSetupComponent,
})

function InviteSetupComponent() {
	const { t } = useTranslation('common')
	const { invitation_token, email, organisation_name } = Route.useSearch()
	const { clearRedirect } = usePositionStorage()
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [verification, setVerification] = useState<{ email: string; pendingAuthenticationToken: string } | null>(null)
	const [orgSelection, setOrgSelection] = useState<
		{
			pendingAuthenticationToken: string
			organizations: Array<{ id: string; name: string }>
		} | null
	>(null)
	const setPasswordMutation = useAuthSetPasswordByInvitation()

	async function handleSubmit(event: FormEvent) {
		event.preventDefault()
		setError(null)

		if (password !== confirmPassword) {
			setError(t('auth.inviteSetup.passwordsMismatch', 'Passwords do not match'))
			return
		}

		try {
			await setPasswordMutation.mutateAsync({
				data: {
					invitationToken: invitation_token,
					password,
				},
			})
			// Auth response sets the refresh cookie, invitation is already accepted — go to company
			window.location.href = '/company'
		} catch (err) {
			if (isAxiosError<AuthSetPasswordByInvitation403>(err) && err.response?.status === 403) {
				const data = err.response.data
				// Invitation was already accepted by the backend — clear the redirect
				// so post-auth flows (email verification, org selection) go to /company
				clearRedirect()
				if (data.code === 'email_verification_required' && data.pending_authentication_token) {
					setVerification({
						email: data.email ?? email,
						pendingAuthenticationToken: data.pending_authentication_token,
					})
					return
				}
				if (
					data.code === 'organization_selection_required'
					&& data.pending_authentication_token
					&& data.organizations
				) {
					setOrgSelection({
						pendingAuthenticationToken: data.pending_authentication_token,
						organizations: data.organizations,
					})
					return
				}
			}
			const status = isAxiosError(err) ? err.response?.status : undefined
			if (status && status >= 400 && status < 500) {
				logger.warn('Set password by invitation failed', {
					category: 'auth',
					data: { action: 'invite-set-password', status },
				})
			} else if (!status || status >= 500) {
				logger.error(err, { category: 'auth', data: { action: 'invite-set-password' } })
			}
			setError(
				extractErrorMessage(err, t('auth.inviteSetup.errorDefault', 'Something went wrong. Please try again.')),
			)
		}
	}

	if (orgSelection) {
		return (
			<AuthLayout>
				<OrganizationList
					organizations={orgSelection.organizations}
					onSelect={(org) => {
						void (async () => {
							try {
								const { data } = await authSelectOrganisation({
									pendingAuthenticationToken: orgSelection.pendingAuthenticationToken,
									organisationId: org.id,
								}, { withCredentials: true })
								const slug = data.organisationSlug
								window.location.href = slug ? `/company/${slug}` : '/company'
							} catch (err) {
								if (
									isAxiosError<AuthSelectOrganisationError>(err)
									&& err.response?.status && err.response.status >= 400
									&& err.response.status < 500
								) {
									logger.warn('Recoverable error on org-selection', {
										category: 'auth',
										data: {
											action: 'org-selection',
											status: err.response.status,
											message: err.response.data?.message,
										},
									})
								} else {
									logger.error(err, { category: 'auth', data: { action: 'org-selection' } })
								}
								setOrgSelection(null)
								setError(t('auth.inviteSetup.errorDefault', 'Something went wrong. Please try again.'))
							}
						})()
					}}
				/>
			</AuthLayout>
		)
	}

	if (verification) {
		return (
			<AuthLayout>
				<EmailVerificationForm
					email={verification.email}
					pendingAuthenticationToken={verification.pendingAuthenticationToken}
					onOrganizationSelectionRequired={(data) => {
						setVerification(null)
						setOrgSelection(data)
					}}
				/>
			</AuthLayout>
		)
	}

	return (
		<AuthLayout>
			<div className="w-full max-w-lg mx-auto p-8">
				<JobillaBrandLogo />

				<div className="mt-8">
					<h1 className="text-3xl font-semibold text-gray-800 tracking-[0.01em]">
						{t('auth.inviteSetup.title', 'Set up your account')}
					</h1>
					<p className="mt-3 text-gray-600">
						{t(
							'auth.inviteSetup.description',
							"You've been invited to join {{organisationName}}. Set up your account to get started.",
							{ organisationName: organisation_name },
						)}
					</p>
				</div>

				<div className="mt-8 space-y-6">
					<div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
						<p className="text-sm text-gray-500">
							{t('auth.inviteSetup.emailLabel', 'Your email')}
						</p>
						<p className="mt-1 text-base font-medium text-gray-800">
							{email}
						</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-6">
						<AuthErrorAlert message={error} />

						<AuthInput
							label={t('auth.inviteSetup.passwordLabel', 'Password')}
							type="password"
							placeholder=""
							value={password}
							onChange={setPassword}
							required={true}
							minLength={10}
							autoComplete="new-password"
						/>

						<AuthInput
							label={t('auth.inviteSetup.confirmPasswordLabel', 'Confirm password')}
							type="password"
							placeholder=""
							value={confirmPassword}
							onChange={setConfirmPassword}
							required={true}
							minLength={10}
							autoComplete="new-password"
						/>

						<AuthSubmitButton isLoading={setPasswordMutation.isPending}>
							{t('auth.inviteSetup.submit', 'Create account')}
						</AuthSubmitButton>
					</form>
				</div>
			</div>
		</AuthLayout>
	)
}
